'use client';

import { parsePhoneNumberFromString, AsYouType, type CountryCode } from 'libphonenumber-js';

/**
 * Basic format check: digits, spaces, +, -, ()
 */
const BASIC_PHONE_RE = /^[+]?[\d\s()-]+$/;

export function isBasicPhoneFormat(value: string): boolean {
  if (!value || !value.trim()) return true; // empty is OK
  return BASIC_PHONE_RE.test(value.trim());
}

/**
 * Validate and normalize a phone number using libphonenumber-js.
 * Returns null if invalid, or the normalized E.164-formatted number if valid.
 *
 * If country is provided (ISO 3166-1 alpha-2, e.g. "CZ", "DE", "SK"),
 * it's used as the default region for numbers without a country code.
 */
export function validateAndNormalizePhone(value: string, country?: string): { valid: boolean; normalized?: string; error?: string } {
  const trimmed = value.trim();
  if (!trimmed) return { valid: true, normalized: '' };

  // 1. Basic format check
  if (!isBasicPhoneFormat(trimmed)) {
    return { valid: false, error: 'invalidFormat' };
  }

  // 2. Parse with libphonenumber-js
  const countryCode = country && country.length === 2 ? country.toUpperCase() as CountryCode : undefined;
  const parsed = parsePhoneNumberFromString(trimmed, countryCode);
  if (!parsed || !parsed.isValid()) {
    return { valid: false, error: 'invalidNumber' };
  }

  // 3. Return normalized E.164 format
  return { valid: true, normalized: parsed.format('INTERNATIONAL') };
}

/**
 * Guess ISO 3166-1 alpha-2 country code from a free-text country name
 * (Czech/German/English names as stored on accounts/contacts).
 * Returns undefined when nothing matches — caller falls back to env default.
 */
export function guessCountryCode(countryName?: string | null): string | undefined {
  const c = (countryName || '').toLowerCase();
  if (!c) return undefined;
  if (c.includes('česk') || c.includes('czech') || c === 'cz' || c.includes(' cz')) return 'CZ';
  if (c.includes('deutsch') || c.includes('german') || c.includes('němec') || c === 'de') return 'DE';
  if (c.includes('sloven')) return 'SK';
  if (c.includes('polsk') || c.includes('poland') || c === 'pl') return 'PL';
  if (c.includes('aust') || c.includes('österreich') || c.includes('rakous')) return 'AT';
  if (c.includes('franc') || c.includes('french')) return 'FR';
  if (c.includes('ital') || c.includes('itáli')) return 'IT';
  if (c.includes('spain') || c.includes('španěl')) return 'ES';
  if (c.includes('hungary') || c.includes('maďar')) return 'HU';
  if (c.includes('netherlands') || c.includes('nizozem') || c.includes('holandsk')) return 'NL';
  if (c.includes('belgium') || c.includes('belgi')) return 'BE';
  if (c.includes('swiss') || c.includes('švýcar')) return 'CH';
  if (c.includes('ukrain') || c.includes('ukrajin')) return 'UA';
  if (c.includes('britain') || c.includes('kingdom') || c.includes('angli')) return 'GB';
  if (c.includes('usa') || c.includes('united states') || c.includes('ameri')) return 'US';
  return undefined;
}

/**
 * Format phone as you type (for live input feedback)
 */
export function formatPhoneAsYouType(value: string, country?: string): string {
  if (!value) return '';
  const countryCode = country && country.length === 2 ? country.toUpperCase() as CountryCode : undefined;
  const formatter = new AsYouType(countryCode);
  return formatter.input(value);
}
